import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { MochiInput } from '@autopass-web/mochi-component';
import { citizenDigitalNumberStrategy } from '@autopass-web/mochi-utils';

import { useImperativeForm } from '../../../hooks/use-bind-async-submit';
import { emailSchema } from '../utils';
import { EmailInput } from './UI';

import type { ReceiptInfoBase } from '../../../configs/receipts';
import type { EmailFieldConfig, ReceiptCompProps } from '../utils';

export interface CarrierCitizenDigitalReceiptInfo extends ReceiptInfoBase {
  type: 'carrier_citizen_digital';
  citizen_digital_number: string;
  email: string;
}

type CarrierCitizenDigitalReceiptForm = Omit<
  CarrierCitizenDigitalReceiptInfo,
  'type'
>;

type CarrierCitizenDigitalReceiptProps = ReceiptCompProps<{
  emailFieldConfig: EmailFieldConfig;
}>;

export const CarrierCitizenDigitalReceipt = ({
  controlRef,
  emailFieldConfig = 'default',
  storage,
  formMode,
}: CarrierCitizenDigitalReceiptProps) => {
  const schema =
    emailFieldConfig === 'never'
      ? z.object({
          citizen_digital_number: citizenDigitalSchema,
        })
      : z.object({
          citizen_digital_number: citizenDigitalSchema,
          email: emailSchema.or(z.string().length(0)),
        });

  const {
    register,
    formState: { errors },
    handleSubmit,
    trigger,
    clearErrors,
    subscribe,
  } = useForm<CarrierCitizenDigitalReceiptForm>({
    defaultValues: { ...(storage?.get?.() ?? {}) },
    mode: formMode ?? 'onSubmit',
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    const cb = subscribe({
      formState: {
        values: true,
      },
      callback: ({ values }) => {
        const receiptInfo = storage?.get?.();

        storage?.set({
          ...receiptInfo,
          ...values,
          type: 'carrier_citizen_digital',
        });
      },
    });

    return () => cb();
  }, [subscribe]);

  const formProps = useImperativeForm(
    controlRef,
    errors,
    (submit) => {
      return handleSubmit((data) =>
        submit({ ...data, type: 'carrier_citizen_digital' }),
      );
    },
    'carrier_citizen_digital',
  );

  return (
    <form {...formProps}>
      <MochiInput
        variant="grey"
        inputMode="numeric"
        placeholder="請輸入自然人載具"
        maxLength={8}
        errorMessage={errors?.citizen_digital_number?.message}
        {...register('citizen_digital_number', {
          onBlur() {
            trigger('citizen_digital_number');
          },
          onChange(e) {
            const value = e.target.value;
            clearErrors('citizen_digital_number');
            value.length > 8 && trigger('citizen_digital_number');
          },
        })}
      />
      <EmailInput
        config={emailFieldConfig}
        errorMessage={errors.email?.message}
        hint={'請填寫 Email 信箱，成功付款後將寄送三聯式電子發票給您'}
        {...register('email')}
      />
    </form>
  );
};

const citizenDigitalSchema = z
  .string()
  .min(1, {
    message: '此欄位為必填',
  })
  .refine((v) => citizenDigitalNumberStrategy(v), {
    message: '輸入格式錯誤，由 2 碼英文 + 14 碼數字組成',
  });
